'use client';

import { ChevronDown, LoaderCircle } from 'lucide-react';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
	completionPath,
	createViewTracker,
	getCandidateReportAction,
	reportLabel,
	reportVisible,
	unwrapCandidateResult,
} from '@/features/candidate-interview';
import type {
	CandidateInterviewHistoryItemV1,
	CandidateInterviewReportEnvelopeV1,
} from '@/shared/api';
import { cn } from '@/shared/utils';

import { CandidateReport, type CandidateTrack } from './candidate-report';

export function CandidateHistoryRow({
	interviewId,
	attempt,
	open,
	onToggle,
	onEvent,
	variant = 'legacy',
}: {
	interviewId: string;
	attempt: CandidateInterviewHistoryItemV1;
	open: boolean;
	onToggle: () => void;
	onEvent: CandidateTrack;
	variant?: 'legacy' | 'workspace';
}) {
	const workspace = variant === 'workspace';
	const [envelope, setEnvelope] =
		useState<CandidateInterviewReportEnvelopeV1>();
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const mounted = useRef(false);
	const inFlight = useRef(false);
	const seen = useRef(createViewTracker());
	const visible = reportVisible(attempt.action, attempt.redirectReport);
	const status = envelope?.status ?? attempt.reportStatus;
	const load = useCallback(async () => {
		if (inFlight.current) return;
		inFlight.current = true;
		setLoading(true);
		try {
			const next = unwrapCandidateResult(
				await getCandidateReportAction(attempt.id),
			);
			if (mounted.current) {
				setEnvelope(next);
				setError('');
			}
		} catch {
			if (mounted.current) {
				setError('Не удалось загрузить отчёт. Попробуйте ещё раз.');
				if (seen.current('request_error'))
					onEvent('request_error', {
						action: 'report',
						outcome: 'request_failed',
					});
			}
		} finally {
			inFlight.current = false;
			if (mounted.current) setLoading(false);
		}
	}, [attempt.id, onEvent]);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	useEffect(() => {
		if (visible && open && !envelope) void load();
	}, [visible, open, envelope, load]);
	useEffect(() => {
		if (!visible || status !== 'pending' || error) return;
		const timer = setInterval(() => void load(), 5000);
		return () => clearInterval(timer);
	}, [visible, status, error, load]);
	useEffect(() => {
		if (open && envelope && seen.current(`view:${status}`))
			onEvent('report_view', {
				reportStatus: status,
				outcome: envelope.report?.outcome,
			});
	}, [open, envelope, status, onEvent]);
	const label = envelope?.report
		? reportLabel(envelope.report)
		: reportLabel({ outcomeLabel: attempt.outcomeLabel, grade: attempt.grade });
	const graded = envelope
		? envelope.report?.outcome === 'graded'
		: !!attempt.grade;
	if (!visible)
		return (
			<article className="rounded-xl bg-white px-6 py-5 text-base">
				<time
					className="block font-semibold"
					dateTime={attempt.finishedAt || attempt.startedAt}
				>
					{new Date(attempt.finishedAt || attempt.startedAt).toLocaleDateString(
						'ru-RU',
					)}
				</time>
				<Link
					className="mt-1 inline-block text-green-700"
					href={completionPath(interviewId, attempt.id)}
					onClick={() => onEvent('result_open')}
				>
					Интервью завершено
				</Link>
			</article>
		);
	return (
		<article
			className={cn(
				'min-w-0 rounded-xl bg-white',
				workspace && 'border border-gray-4/50',
			)}
		>
			<button
				type="button"
				onClick={onToggle}
				aria-expanded={open}
				aria-controls={`report-${attempt.id}`}
				className={cn(
					'flex w-full items-center gap-4 rounded-xl px-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600',
					workspace ? 'py-4 sm:px-7' : 'py-5 sm:px-6',
				)}
			>
				<span className="min-w-0 flex-1">
					<time
						dateTime={attempt.finishedAt || attempt.startedAt}
						className={cn(
							'block text-base',
							workspace ? 'font-medium' : 'font-semibold',
						)}
					>
						{new Date(
							attempt.finishedAt || attempt.startedAt,
						).toLocaleDateString('ru-RU', {
							day: 'numeric',
							month: 'long',
							year: 'numeric',
						})}
					</time>
					<span
						className={cn(
							'mt-1 block',
							workspace ? 'text-sm text-gray-3' : 'text-base',
							!workspace && (graded ? 'text-green-700' : 'text-gray-500'),
						)}
					>
						{status === 'ready'
							? label || 'Отчёт готов'
							: status === 'pending'
								? 'Отчёт готовится…'
								: status === 'error'
									? 'Отчёт не готов'
									: 'Отчёт отсутствует'}
					</span>
				</span>
				<ChevronDown
					size={workspace ? 16 : 20}
					aria-hidden="true"
					className={cn(
						'shrink-0 text-gray-400 transition-transform',
						open && 'rotate-180',
					)}
				/>
			</button>
			{open && (
				<div id={`report-${attempt.id}`}>
					{error && (
						<div
							role="alert"
							className="mx-5 mb-5 rounded-xl bg-red-50 p-4 text-sm text-red-800"
						>
							{error}{' '}
							<button
								type="button"
								disabled={loading}
								className="rounded underline focus-visible:ring-2 focus-visible:ring-red-500"
								onClick={() => {
									onEvent('retry', { action: 'report' });
									setError('');
									void load();
								}}
							>
								Обновить
							</button>
						</div>
					)}
					{!envelope && !error && (
						<div
							role="status"
							className="flex items-center gap-3 px-6 pb-6 text-gray-500"
						>
							<LoaderCircle
								className="size-5 animate-spin motion-reduce:animate-none"
								aria-hidden="true"
							/>
							Загружаем отчёт…
						</div>
					)}
					{envelope && (
						<CandidateReport
							variant={variant}
							interviewId={interviewId}
							envelope={envelope}
							onEvent={onEvent}
						/>
					)}
				</div>
			)}
		</article>
	);
}
